import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { useIssuerPortfolioTotalCashInOutChartByIdQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { ChartEmptyState, ChartMultiBar, ChartMultiBarData, InfoCard, Spinner } from 'ui';

interface ChartTotalCashInAndOutProps {
  portfolioId: number;
}

export const ChartTotalCashInAndOut = ({ portfolioId }: ChartTotalCashInAndOutProps) => {
  const { registerCurrency = 'USD' } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const {
    isFetching,
    isLoading,
    isError,
    data: dataTotalCashInOutChart,
  } = useIssuerPortfolioTotalCashInOutChartByIdQuery({
    portfolio_id: portfolioId,
  });

  const chartData = useMemo<ChartMultiBarData[]>(() => {
    if (!dataTotalCashInOutChart?.data?.length) return [];

    const cashIn: number[] = [];
    const cashOut: number[] = [];
    const dates: Array<number | string> = [];
    dataTotalCashInOutChart?.data?.forEach(item => {
      cashIn.push(item.cash_in);
      cashOut.push(item.cash_out);
      dates.push(item.date);
    });

    const items = ['Cash In', 'Cash Out']?.map<ChartMultiBarData>(category => ({
      name: category,
      data: dates.map((date, i) => ({
        date,
        value: category === 'Cash In' ? cashIn[i] : category === 'Cash Out' ? cashOut[i] : 0,
      })),
    }));

    return items;
  }, [dataTotalCashInOutChart?.data]);

  return (
    <InfoCard
      title="Total cash in and out"
      classes={{ content: 'w-full p-0 px-6 pt-4' }}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="md" />}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!chartData?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {!isLoading && chartData?.length > 0 && (
          <ChartMultiBar
            height="370"
            items={chartData}
            currency={registerCurrency ? registerCurrency : ''}
          />
        )}
      </div>
    </InfoCard>
  );
};
